<template>
    <q-select use-input filled v-model="vehicle" input-debounce="0" :label="label" :hint="hint" :options="vehicles"
        @filter="filterVehicle" @update:modelValue="selectVehicle" :dense="true" :clearable="true" :loading="loading"
        :readonly="readonly">
        <template v-slot:no-option>
            <q-item>
                <q-item-section class="text-grey">
                    Sin resultados
                </q-item-section>
            </q-item>
        </template>
    </q-select>
</template>


<script lang="ts">
import { defineComponent, onMounted, reactive, toRefs, watch } from 'vue';
import { useQuasar } from 'quasar';

import { OptionDto, FindVehicleAutocompleteDto } from '@/dtos';
import vehicleService from '@/services/fleet-management/vehicle.service';
import { errorNotify } from '@/helpers/utilities';

interface Props {
    clear?: boolean;
    vehicle_id?: number;
    label?: string;
    hint?: string;
    company_id?: number;
    vehicle_group_id?: number;
    readonly?: boolean;
}

export default defineComponent({
    name: 'VehicleAutocompleteComponent',
    props: {
        clear: { type: Boolean },
        vehicle_id: { type: Number },
        label: { type: String },
        hint: { type: String },
        company_id: { type: Number },
        vehicle_group_id: { type: Number },
        readonly: { type: Boolean, default: false }
    },
    emits: ['vehicle_selected'],
    setup(props: Props, { emit }) {
        const $q = useQuasar();

        const config = reactive({
            loading: false,
            label: props.label ?? 'Vehículo',
            hint: props.hint,
            company_id: props.company_id,
            vehicle_group_id: props.vehicle_group_id,
            searcher: ''
        });

        const data = reactive({
            vehicle: '',
            vehicles: [] as OptionDto[],
            vehicle_id: props.vehicle_id
        });

        const fetchVehicles = async () => {
            try {
                config.loading = true;

                const params: FindVehicleAutocompleteDto = {
                    company_id: config.company_id,
                    vehicle_group_id: config.vehicle_group_id,
                    searcher: config.searcher
                }
                const vehicles = await vehicleService.findAllAutocomplete(params);

                data.vehicles = [];
                for (const vehicle of vehicles.data) {
                    const opt: OptionDto = {
                        label: vehicle.name,
                        value: vehicle.id
                    }

                    data.vehicles.push(opt);
                }

                if (data.vehicle_id && data.vehicle_id > 0) {
                    const vehicle = data.vehicles.find(x => x.value == data.vehicle_id);
                    if (vehicle) {
                        data.vehicle = vehicle.label;
                        config.loading = false;
                        return;
                    }

                    await fetchVehicle(data.vehicle_id);
                }

                config.loading = false;
            } catch (error) {
                console.log(error);
                $q.notify(errorNotify('No se pudo obtener la información de los vehículos'));
            }
        }

        const fetchVehicle = async (vehicle_id: number) => {
            try {
                const vehicleResult = await vehicleService.findOne(vehicle_id);
                const opt: OptionDto = {
                    label: vehicleResult.data.name,
                    value: vehicleResult.data.id
                }

                data.vehicles.push(opt);
                data.vehicle = opt.label;
            } catch (error) {
                console.log(error);
                $q.notify(errorNotify('No se pudo obtener la información del vehículo'));
            }
        }

        const selectVehicle = (option: OptionDto) => {
            emit('vehicle_selected', option);
        }

        const filterVehicle = (val: string, update: (fn: () => void) => void) => {
            if (val === '' && data.vehicles.length == 0) {
                update(async () => {
                    config.searcher = '';
                    await fetchVehicles();
                })
                return;
            }

            update(async () => {
                const needle = val.toLowerCase();
                if (needle.length >= 2) {
                    config.searcher = needle;
                    data.vehicle_id = undefined;
                    await fetchVehicles();
                }
            })
        }

        onMounted(async () => {
            await fetchVehicles();
        });

        watch([() => props.clear, () => props.vehicle_id, () => props.company_id, () => props.vehicle_group_id],
            async ([newVal, newValId, newCompanyId, newVehicleGroupId], [oldVal, oldValId, oldCompanyId, oldVehicleGroupId]) => {
                if (!newCompanyId && newCompanyId != oldCompanyId) {
                    config.company_id = undefined;
                    data.vehicle_id = undefined;

                    data.vehicle = '';
                    await fetchVehicles();
                    return;
                }
                
                if (newVal != oldVal) {
                    data.vehicle = '';
                    data.vehicle_id = undefined;

                    config.searcher = '';
                    await fetchVehicles();
                    return;
                }

                if (newValId && newValId > 0 &&
                    ((newCompanyId == oldCompanyId && newVehicleGroupId == oldVehicleGroupId)
                        || (oldCompanyId == 0 || oldVehicleGroupId == 0)
                    )) {

                    const vehicleGroup = data.vehicles.find(x => x.value == newValId);
                    if (vehicleGroup) {
                        data.vehicle = vehicleGroup.label;
                        return;
                    }

                    await fetchVehicle(newValId);
                    return;
                }

                if (newCompanyId && newCompanyId != oldCompanyId) {
                    config.company_id = newCompanyId;
                    data.vehicle = '';
                    data.vehicle_id = undefined;

                    config.searcher = '';
                    await fetchVehicles();
                }

                if (newVehicleGroupId && newVehicleGroupId != oldVehicleGroupId) {
                    config.vehicle_group_id = newVehicleGroupId;
                    data.vehicle = '';
                }

                await fetchVehicles();
            });

        return {
            ...toRefs(config),
            ...toRefs(data),
            filterVehicle,
            selectVehicle,
        }
    }
});
</script>